import type { CSSProperties, FC, ReactNode } from 'react'
import type { Testimonial, Tone } from '@/types'

import { useId, useState } from 'react'
import { cn } from '@/lib/utils'
import { SectionLabel } from '@/components/generals/section-label'
import { SplitWords } from '@/components/generals/split-words'
import { Stars } from '@/components/generals/stars'

interface QuoteTabsProps {
    index?: string
    title: string
    intro?: string
    items: Testimonial[]
    tone?: Tone
    id?: string
}

const DARK: Tone[] = ['night', 'deep', 'raised']

/** One voice at a time, set large. The portraits double as tabs. */
export const QuoteTabs: FC<QuoteTabsProps> = ({ index, title, intro, items, tone = 'night', id = 'kundehistorier' }): ReactNode => {
    const base = useId().replace(/[^a-zA-Z0-9_-]/g, '')
    const [active, setActive] = useState(0)
    const item = items[active]

    return (
        <section
            id={id}
            className={cn(`tone-${tone}`, DARK.includes(tone) && 'grain', 'relative overflow-hidden py-24 md:py-36')}
            data-header={DARK.includes(tone) ? 'dark' : 'light'}
        >
            <div className='wrap grid grid-cols-12 gap-x-6 gap-y-14'>
                <div className='col-span-12 lg:col-span-4'>
                    <SectionLabel index={index}>Kundehistorier</SectionLabel>
                    <h2 className='t-h1 mt-6 balance' data-reveal='words'>
                        <SplitWords text={title} />
                    </h2>
                    {intro && <p className='mt-6 max-w-sm text-tone-muted'>{intro}</p>}

                    <div role='tablist' aria-label='Velg kundehistorie' className='mt-10 flex flex-col border-t border-tone-line'>
                        {items.map((entry, i) => (
                            <button
                                key={`${entry.name}-${i}`}
                                id={`${base}-tab-${i}`}
                                type='button'
                                role='tab'
                                aria-selected={i === active}
                                aria-controls={`${base}-panel`}
                                tabIndex={i === active ? 0 : -1}
                                onClick={() => setActive(i)}
                                onKeyDown={event => {
                                    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
                                        event.preventDefault()
                                        setActive((active + 1) % items.length)
                                    }

                                    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
                                        event.preventDefault()
                                        setActive((active - 1 + items.length) % items.length)
                                    }
                                }}
                                className={cn(
                                    'group flex items-center gap-4 border-b border-tone-line py-4 text-left transition-opacity duration-500',
                                    i === active ? 'opacity-100' : 'opacity-55 hover:opacity-90'
                                )}
                            >
                                {entry.image && (
                                    <span className='block size-12 shrink-0 overflow-hidden bg-tone-line'>
                                        <img src={entry.image} alt='' loading='lazy' decoding='async' className='size-full object-cover' />
                                    </span>
                                )}
                                <span className='flex-1'>
                                    <span className='block font-semibold'>{entry.name}</span>
                                    <span className='block text-sm text-tone-muted'>{entry.company}</span>
                                </span>
                                <span className={cn('h-px w-8 bg-tone-accent transition-transform duration-500 ease-out-quint origin-left', i === active ? 'scale-x-100' : 'scale-x-0')} aria-hidden='true' />
                            </button>
                        ))}
                    </div>
                </div>

                <div
                    id={`${base}-panel`}
                    role='tabpanel'
                    aria-labelledby={`${base}-tab-${active}`}
                    className='col-span-12 grid grid-cols-12 gap-x-6 gap-y-10 lg:col-span-8'
                >
                    <figure key={active} className='col-span-12 md:col-span-8'>
                        <div className='flex items-center gap-4 text-tone-ink'>
                            <Stars />
                            {item.highlight && <span className='t-label text-tone-muted'>{item.highlight}</span>}
                        </div>
                        <blockquote className='mt-8'>
                            <p className='t-h2 load-words t-serif' style={{ '--load-delay': '0s' } as CSSProperties}>
                                «<SplitWords text={item.quote} />»
                            </p>
                        </blockquote>
                        <figcaption className='mt-10 text-sm'>
                            <span className='font-semibold'>{item.name}</span>
                            <span className='text-tone-muted'> · {item.company}</span>
                        </figcaption>
                    </figure>

                    {item.image && (
                        <div key={`img-${active}`} className='quote-tab-image col-span-6 col-start-7 aspect-[3/4] overflow-hidden bg-tone-line md:col-span-4 md:col-start-auto'>
                            <img src={item.image} alt={`${item.name}, ${item.company}`} loading='lazy' decoding='async' className='size-full object-cover' />
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}
